import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2,
  Globe,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  ChevronUp,
  ChevronDown
} from '../components/Icons';
import Navigation from '../components/Navigation';
import { apiGet, apiPost, apiDelete } from '../utils/api';
import { downloadInvoicePdf } from '../utils/downloadInvoicePdf';
import { getFbrStatusColor, getFbrStatusIcon } from '../utils/fbrStatus';
import toast from 'react-hot-toast';

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [submittingFbr, setSubmittingFbr] = useState({});
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedError, setSelectedError] = useState('');
  
  // Filter states
  const [fbrStatusFilter, setFbrStatusFilter] = useState('');
  const [provinceFilter, setProvinceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [minItems, setMinItems] = useState('');
  const [maxItems, setMaxItems] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Dynamic filter values from backend
  const [filterValues, setFilterValues] = useState({
    fbr_statuses: [],
    provinces: [],
    statuses: [],
    amount_range: { min: 0, max: 0 },
    item_count_range: { min: 0, max: 0 }
  });

  useEffect(() => {
    fetchFilterValues();
  }, []);

  useEffect(() => {
    fetchInvoices();
    setSelectedInvoices([]);
    setSelectAll(false);
  }, [currentPage, searchTerm, fbrStatusFilter, provinceFilter, statusFilter, minAmount, maxAmount, minItems, maxItems, dateFrom, dateTo, sortField, sortDirection, itemsPerPage]);

  const fetchFilterValues = async () => {
    try {
      const response = await apiGet('/api/invoices/filter-values');
      if (response.ok) {
        setFilterValues(response.data);
      }
    } catch (error) {
      console.error('Error fetching filter values:', error);
    }
  };

  const formatFbrApiError = (responseData) => {
    if (!responseData) return 'Failed to submit invoice to FBR';

    const detail = responseData.detail;
    if (!detail) {
      return responseData.message || responseData.error || 'Failed to submit invoice to FBR';
    }

    if (typeof detail === 'string') {
      return detail;
    }

    const messages = [];
    if (detail.message) {
      messages.push(detail.message);
    }

    const parsedResponse = detail.parsed_response || detail.parsedResponse || null;
    if (parsedResponse) {
      if (parsedResponse.error) {
        messages.push(parsedResponse.error);
      }
      if (Array.isArray(parsedResponse.failed_items) && parsedResponse.failed_items.length > 0) {
        parsedResponse.failed_items.forEach((item) => {
          const itemNo = item.item_sno || item.itemSNo || 'Unknown';
          const itemError = item.error || item.status || '';
          if (itemError) {
            messages.push(`Item ${itemNo}: ${itemError}`);
          }
        });
      }
    }

    const rawResponse = detail.fbr_response && detail.fbr_response.validationResponse;
    if (!messages.length && rawResponse) {
      if (rawResponse.error) {
        messages.push(rawResponse.error);
      }
      if (Array.isArray(rawResponse.invoiceStatuses)) {
        rawResponse.invoiceStatuses.forEach((itemStatus) => {
          if (itemStatus?.error) {
            const itemNum = itemStatus.itemSNo || 'Unknown';
            messages.push(`Item ${itemNum}: ${itemStatus.error}`);
          }
        });
      }
    }

    return messages.length ? messages.join(' | ') : detail.message || 'Failed to submit invoice to FBR';
  };

  const fetchInvoices = async () => {
    try {
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        sort_by: sortField,
        sort_order: sortDirection
      };
      
      if (searchTerm) params.search = searchTerm;
      if (fbrStatusFilter) params.fbr_status = fbrStatusFilter;
      if (provinceFilter) params.province = provinceFilter;
      if (statusFilter) params.status_filter = statusFilter;
      if (minAmount) params.min_amount = minAmount;
      if (maxAmount) params.max_amount = maxAmount;
      if (minItems) params.min_items = minItems;
      if (maxItems) params.max_items = maxItems;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await apiGet('/api/invoices/', params);

      if (response.ok) {
        setInvoices(response.data.invoices || []);
        setTotalInvoices(response.data.total || 0);
        setTotalPages(Math.ceil((response.data.total || 0) / itemsPerPage));
      } else {
        console.error('Error fetching invoices:', response.data);
        toast.error('Failed to fetch invoices');
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Error fetching invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronUp className="h-4 w-4 text-slate-400" />;
    }
    return sortDirection === 'asc' ? 
      <ChevronUp className="h-4 w-4 text-amber-600" /> :
      <ChevronDown className="h-4 w-4 text-amber-600" />;
  };

  const handleSubmitToFbr = async (invoiceId) => {
    setSubmittingFbr(prev => ({ ...prev, [invoiceId]: true }));
    
    try {
      const response = await apiPost(`/api/invoices/${invoiceId}/submit-to-fbr`, {});

      if (response.ok) {
        const result = response.data;
        toast.success(`Invoice submitted to FBR successfully! FBR Invoice ID: ${result.fbr_invoice_id}`);
        fetchInvoices(); // Refresh the list
      } else {
        const errorMessage = formatFbrApiError(response.data) || 'Failed to submit to FBR';
        toast.error(`FBR Error: ${errorMessage}`, { duration: 8000 });
      }
    } catch (error) {
      console.error('Error submitting to FBR:', error);
      let errorMessage = 'Failed to submit to FBR';
      if (error.response?.data) {
        errorMessage = formatFbrApiError(error.response.data);
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(`FBR Error: ${errorMessage}`, { duration: 8000 });
    } finally {
      setSubmittingFbr(prev => ({ ...prev, [invoiceId]: false }));
    }
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      await downloadInvoicePdf(invoiceId);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(error.message || 'Error downloading PDF');
    }
  };


  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to deactivate this invoice? It will be hidden from the invoices list but can be reactivated later.')) return;

    try {
      const response = await apiDelete(`/api/invoices/${invoiceId}`);
      if (response.ok) {
        toast.success('Invoice deactivated successfully! It has been hidden from the invoices list.');
        fetchInvoices();
      } else {
        const errorMessage = response.data?.detail || response.data?.message || 'Failed to deactivate invoice';
        toast.error(`Failed to deactivate invoice: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error deactivating invoice:', error);
      toast.error('Error deactivating invoice');
    }
  };

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => prev.includes(invoiceId) ? prev.filter(id => id !== invoiceId) : [...prev, invoiceId]);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoices.map(inv => inv.id));
    }
    setSelectAll(!selectAll);
  };

  const bulkDeactivateInvoices = async () => {
    if (selectedInvoices.length === 0) {
      toast.error('Please select invoices to deactivate');
      return;
    }
    if (!window.confirm(`Are you sure you want to deactivate ${selectedInvoices.length} invoice(s)?`)) return;
    
    try {
      const response = await apiPost('/api/invoices/bulk-deactivate', selectedInvoices);
      if (response.ok) {
        toast.success(response.data.message || `${selectedInvoices.length} invoice(s) deactivated successfully!`);
        setSelectedInvoices([]);
        setSelectAll(false);
        fetchInvoices();
      } else {
        toast.error(response.data.detail || 'Error deactivating invoices');
      }
    } catch (error) {
      toast.error('Error deactivating invoices');
    }
  };


  return (
    <Navigation>
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white shadow-sm border border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Invoices</h1>
                <p className="text-slate-600">Manage and track your invoices</p>
              </div>
              <Link
                to="/create-invoice"
                className="bg-slate-700 text-white px-4 py-2 rounded-lg hover:bg-slate-800 flex items-center shadow-sm hover:shadow-md transition-all"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Invoice
              </Link>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search and Filters */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 mb-6">
            <div className="p-4">
              {/* Filter Row 1 */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
                <input
                  type="text"
                  placeholder="Search invoices..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <select
                  value={fbrStatusFilter}
                  onChange={(e) => setFbrStatusFilter(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All FBR Status</option>
                  {filterValues.fbr_statuses.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
                <select
                  value={provinceFilter}
                  onChange={(e) => setProvinceFilter(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All Provinces</option>
                  {filterValues.provinces.map((province) => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All Status</option>
                  {filterValues.statuses.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
              
              {/* Filter Row 2 - Amount & Item Count */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
                <input
                  type="number"
                  placeholder={`Min Amount (${filterValues.amount_range.min})`}
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <input
                  type="number"
                  placeholder={`Max Amount (${filterValues.amount_range.max})`}
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <input
                  type="number"
                  placeholder={`Min Items (${filterValues.item_count_range.min})`}
                  value={minItems}
                  onChange={(e) => setMinItems(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <input
                  type="number"
                  placeholder={`Max Items (${filterValues.item_count_range.max})`}
                  value={maxItems}
                  onChange={(e) => setMaxItems(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              
              {/* Filter Row 3 - Date Range & Clear */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="From Date"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="To Date"
                />
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFbrStatusFilter('');
                    setProvinceFilter('');
                    setStatusFilter('');
                    setMinAmount('');
                    setMaxAmount('');
                    setMinItems('');
                    setMaxItems('');
                    setDateFrom('');
                    setDateTo('');
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center justify-center"
                >
                  <Filter className="h-4 w-4 mr-1" />
                  Clear Filters
                </button>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                <div className="text-xs text-slate-600">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalInvoices)} of{' '}
                  <span className="font-medium">{totalInvoices}</span> invoices
                </div>
                <nav className="inline-flex rounded-lg shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-1 rounded-l-md border border-slate-300 bg-white text-xs font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="relative inline-flex items-center px-3 py-1 border border-slate-300 bg-white text-xs font-medium text-slate-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-1 rounded-r-md border border-slate-300 bg-white text-xs font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {selectedInvoices.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-700">
                  {selectedInvoices.length} invoice(s) selected
                </span>
                <button
                  onClick={bulkDeactivateInvoices}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Deactivate Selected
                </button>
              </div>
            </div>
          )}

          {/* Invoices Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
                <p className="mt-2 text-slate-600">Loading invoices...</p>
              </div>
            ) : invoices.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <p>No invoices found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-max divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="rounded border-slate-300"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          <button
                            onClick={() => handleSort('invoice_number')}
                            className="flex items-center space-x-1 hover:text-slate-700"
                          >
                            <span>Invoice #</span>
                            {getSortIcon('invoice_number')}
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          <button
                            onClick={() => handleSort('client_name')}
                            className="flex items-center space-x-1 hover:text-slate-700"
                          >
                            <span>Client</span>
                            {getSortIcon('client_name')}
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          <button
                            onClick={() => handleSort('total_amount')}
                            className="flex items-center space-x-1 hover:text-slate-700"
                          >
                            <span>Amount</span>
                            {getSortIcon('total_amount')}
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          <button
                            onClick={() => handleSort('fbr_status')}
                            className="flex items-center space-x-1 hover:text-slate-700"
                          >
                            <span>FBR Status</span>
                            {getSortIcon('fbr_status')}
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          <button
                            onClick={() => handleSort('created_at')}
                            className="flex items-center space-x-1 hover:text-slate-700"
                          >
                            <span>Date</span>
                            {getSortIcon('created_at')}
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-80">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {invoices.map((invoice) => (
                        <tr key={invoice.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedInvoices.includes(invoice.id)}
                              onChange={() => handleSelectInvoice(invoice.id)}
                              className="rounded border-slate-300"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-slate-900">
                              {invoice.invoice_number}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900">{invoice.client_name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-slate-900">
                              Rs. {parseFloat(invoice.total_amount || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getFbrStatusColor(invoice.fbr_status)}`}>
                                {getFbrStatusIcon(invoice.fbr_status)}
                                <span className="ml-1">{invoice.fbr_status?.replace('_', ' ') || 'Not Submitted'}</span>
                              </span>
                              {invoice.fbr_error && (
                                <span 
                                  className="ml-2 text-red-500 cursor-pointer flex items-center p-1 rounded hover:bg-red-50 transition-colors" 
                                  onClick={() => {
                                    setSelectedError(invoice.fbr_error);
                                    setShowErrorModal(true);
                                  }}
                                  title="Click to view full FBR error"
                                >
                                  ⚠️
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                            {(invoice.invoice_date
                              ? new Date(invoice.invoice_date)
                              : new Date(invoice.created_at)
                            ).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium w-80">
                            <div className="flex flex-col gap-2">
                              <div className="flex gap-3">
                                <button
                                  onClick={() => handleSubmitToFbr(invoice.id)}
                                  disabled={submittingFbr[invoice.id] || invoice.fbr_status === 'submitted'}
                                  className="text-purple-600 hover:text-purple-900 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                >
                                  <Globe className="h-4 w-4 inline mr-1" />
                                  {submittingFbr[invoice.id] ? 'Submitting...' : 'Submit to FBR'}
                                </button>
                                <button
                                  onClick={() => handleDownloadPdf(invoice.id)}
                                  className="text-slate-600 hover:text-slate-900 whitespace-nowrap"
                                >
                                  <Download className="h-4 w-4 inline mr-1" />
                                  PDF
                                </button>
                              </div>
                              <div className="flex gap-3">
                                <Link
                                  to={`/invoices/${invoice.id}/view`}
                                  className="text-amber-600 hover:text-amber-800 whitespace-nowrap"
                                >
                                  <Eye className="h-4 w-4 inline mr-1" />
                                  View
                                </Link>
                                <Link
                                  to={`/invoices/${invoice.id}/edit`}
                                  className="text-green-600 hover:text-green-900 whitespace-nowrap"
                                >
                                  <Edit className="h-4 w-4 inline mr-1" />
                                  Edit
                                </Link>
                                <button
                                  onClick={() => handleDelete(invoice.id)}
                                  className="text-red-600 hover:text-red-900 whitespace-nowrap"
                                  title="Deactivate Invoice"
                                >
                                  <Trash2 className="h-4 w-4 inline mr-1" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="bg-white px-4 py-3 border-t border-slate-200 sm:px-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-slate-700">Items per page:</label>
                        <select
                          value={itemsPerPage}
                          onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                          className="border border-slate-300 rounded-lg px-2 py-1 text-sm"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                      <p className="text-sm text-slate-700">
                        Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * itemsPerPage, totalInvoices)}
                        </span>{' '}
                        of <span className="font-medium">{totalInvoices}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-slate-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const page = i + 1;
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === page
                                  ? 'z-10 bg-amber-50 border-amber-500 text-amber-600'
                                  : 'bg-white border-slate-300 text-slate-500 hover:bg-slate-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-slate-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* FBR Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 bg-slate-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={() => setShowErrorModal(false)}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <span className="text-red-600 text-xl">⚠️</span>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-bold text-slate-900" id="modal-title">
                      FBR Validation Error Details
                    </h3>
                    <div className="mt-3 p-3 bg-red-50 rounded border border-red-200 max-h-60 overflow-y-auto">
                      <p className="text-sm text-red-700 whitespace-pre-wrap font-mono break-words leading-relaxed text-left">
                        {selectedError || 'No error details recorded.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-lg border border-slate-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-slate-700 hover:bg-slate-50 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowErrorModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Navigation>
  );
};

export default Invoices; 